import { useState } from 'react';

const NAVY = '#0D2461', AMBER = '#F5A800', BORDER = '#c8d0e0';

export default function EulaModal({ onAccept }) {
  const [scrolled, setScrolled] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const handleScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (atBottom) setScrolled(true);
  };

  const handleAccept = async () => {
    setAccepting(true);
    await onAccept();
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Header */}
        <div style={header}>
          <div style={eyebrow}>The LAB Business Solutions</div>
          <h2 style={title}>End User License Agreement</h2>
          <p style={sub}>
            Please read and scroll through the full agreement before accepting.
            Last Updated: April 8, 2026
          </p>
        </div>

        {/* Scrollable content */}
        <div style={scrollBox} onScroll={handleScroll}>
          <div style={section}>
            <p style={intro}>
              This End User License Agreement ("Agreement") is a legal agreement between you
              (either an individual or a single entity, hereafter "User" or "you") and The LAB
              ("Company") for the use of the Stat Sample Calculator software application,
              including any associated media, printed materials, and online or electronic
              documentation (collectively, the "Software").
            </p>
            <p style={intro}>
              By installing, copying, or otherwise using the Software, you agree to be bound
              by the terms of this Agreement. If you do not agree to the terms of this
              Agreement, do not install or use the Software.
            </p>
          </div>

          {[
            {
              num: '1', title: 'LICENSE GRANT',
              text: `The LAB grants you a revocable, non-exclusive, non-transferable, limited license to download, install, and use the Software solely for your internal business evaluation purposes, strictly in accordance with the terms of this Agreement. This license is granted specifically to assist CPA firms and related professionals in evaluating sample data and does not constitute the sale of the Software.`,
            },
            {
              num: '2', title: 'RESTRICTIONS ON USE',
              text: null,
              bullets: [
                'No Copying/Redistribution: Copy, reproduce, distribute, republish, upload, post, transmit, sublicense, sell, rent, lease, lend, timeshare, or otherwise make the Software available to any third party.',
                'No Modifications/Derivative Works: Modify, adapt, translate, or create derivative works based on the Software.',
                'No Reverse Engineering: Decompile, disassemble, decrypt, reverse compile, reverse engineer, or otherwise attempt to derive the source code or underlying ideas, algorithms, structure, or organization of the Software, except to the extent such restriction is prohibited by applicable law.',
                'No Unauthorized Use: Use the Software for any purpose other than as permitted in Section 1.',
                'Remove Notices: Remove, alter, or obscure any proprietary notice (including any notice of copyright or trademark) of The LAB or its affiliates, partners, or suppliers.',
              ],
            },
            {
              num: '3', title: 'INTELLECTUAL PROPERTY',
              text: `The Software, including without limitation all copyrights, patents, trademarks, trade secrets, and other intellectual property rights are, and shall remain, the sole and exclusive property of The LAB. The LAB reserves all rights not expressly granted to you in this Agreement.`,
            },
            {
              num: '4', title: 'NO PROFESSIONAL ADVICE; ACCURACY DISCLAIMER',
              text: `The Stat Sample Calculator is provided for informational and illustrative purposes only. The outputs and calculations generated by the Software do not constitute professional accounting, tax, legal, or financial advice. The LAB does not guarantee the accuracy, completeness, or timeliness of any calculations, estimates, outputs, or results generated by the Software. You are solely responsible for verifying the accuracy and appropriateness of any results generated by the Software.\n\nDo not use the Software outputs as the sole basis for any tax filings, positions, or reporting, or for any legal claims, without independent professional verification by a qualified advisor. Use of this Software does not create a fiduciary or professional-client relationship between you and The LAB.`,
            },
            {
              num: '5', title: 'USER DATA RESPONSIBILITY; TERMINATION',
              text: `User Data Responsibility. You are solely responsible for (a) any data, content, or information you input, upload, transmit, or otherwise provide in connection with your use of the Software ("User Data"), and (b) verifying the accuracy, completeness, legality, and appropriateness of such User Data. You represent and warrant that you have all rights, permissions, and authority necessary to provide the User Data and to use it with the Software. The LAB is not responsible for User Data, including any errors, omissions, loss, corruption, or unauthorized disclosure arising from your use of the Software. Do not include any taxpayer identifying information in the Stat Sample Calculator application.\n\nTermination. This Agreement shall remain in effect until terminated by you or The LAB. The LAB may, in its sole discretion, at any time and for any or no reason, suspend or terminate this Agreement with or without prior notice. This Agreement will terminate immediately, without prior notice from The LAB, in the event that you fail to comply with any provision of this Agreement. Upon termination, you shall cease all use of the Software and delete all copies thereof from your systems.`,
            },
            {
              num: '6', title: 'NO WARRANTY (AS-IS)',
              text: `THE SOFTWARE IS PROVIDED TO YOU "AS IS" AND "AS AVAILABLE" AND WITH ALL FAULTS AND DEFECTS WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, THE LAB EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. THE LAB PROVIDES NO WARRANTY OR UNDERTAKING THAT THE SOFTWARE WILL MEET YOUR REQUIREMENTS, ACHIEVE ANY INTENDED RESULTS, OR BE ERROR-FREE.`,
            },
            {
              num: '7', title: 'LIMITATION OF LIABILITY',
              text: `NOTWITHSTANDING ANY DAMAGES THAT YOU MIGHT INCUR, THE ENTIRE LIABILITY OF THE LAB UNDER ANY PROVISION OF THIS AGREEMENT AND YOUR EXCLUSIVE REMEDY FOR ALL OF THE FOREGOING SHALL BE LIMITED TO THE AMOUNT ACTUALLY PAID BY YOU FOR THE SOFTWARE (IF ANY). TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE LAB BE LIABLE FOR ANY SPECIAL, INCIDENTAL, INDIRECT, OR CONSEQUENTIAL DAMAGES WHATSOEVER (INCLUDING, BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA, OR BUSINESS INTERRUPTION) ARISING OUT OF OR IN ANY WAY RELATED TO THE USE OF OR INABILITY TO USE THE SOFTWARE.`,
            },
            {
              num: '8', title: 'GOVERNING LAW',
              text: `This Agreement shall be governed by and construed in accordance with the laws of the State of Texas, United States, without regard to its conflict of law principles. Any legal action or proceeding arising under this Agreement will be brought exclusively in the federal or state courts located in Texas.`,
            },
            {
              num: '9', title: 'SEVERABILITY',
              text: `If any provision of this Agreement is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.`,
            },
            {
              num: '10', title: 'CONTACT INFORMATION',
              text: `If you have any questions about this Agreement, please contact:\n\nThe LAB\nWebsite: www.theLAB.tax`,
            },
          ].map(sec => (
            <div key={sec.num} style={section}>
              <div style={sectionTitle}>{sec.num}. {sec.title}</div>
              {sec.text && sec.text.split('\n\n').map((para, i) => (
                <p key={i} style={para.toUpperCase() === para ? legalPara : bodyPara}>{para}</p>
              ))}
              {sec.bullets && (
                <ul style={bulletList}>
                  {sec.bullets.map((b, i) => (
                    <li key={i} style={bulletItem}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Scroll hint */}
        {!scrolled && (
          <div style={scrollHint}>↓ Scroll to read the full agreement before accepting</div>
        )}

        {/* Footer */}
        <div style={footer}>
          <div style={footerNote}>
            By clicking "I Accept", you agree to be bound by the terms of this End User License Agreement.
          </div>
          <button
            style={{ ...acceptBtn, opacity: scrolled ? 1 : 0.4, cursor: scrolled ? 'pointer' : 'not-allowed' }}
            onClick={handleAccept}
            disabled={!scrolled || accepting}
          >
            {accepting ? 'Saving…' : 'I Accept — Continue to The LAB Platform →'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay    = { position:'fixed', inset:0, background:'rgba(13,36,97,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:24 };
const modal      = { background:'white', borderRadius:12, width:'100%', maxWidth:720, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 80px rgba(0,0,0,0.4)' };
const header     = { padding:'32px 36px 20px', borderBottom:`1.5px solid ${BORDER}`, flexShrink:0 };
const eyebrow    = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:AMBER, marginBottom:6 };
const title      = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:NAVY, margin:'0 0 6px' };
const sub        = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', margin:0 };
const scrollBox  = { flex:1, overflowY:'auto', padding:'24px 36px' };
const section    = { marginBottom:24 };
const sectionTitle={ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, color:NAVY, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.04em' };
const bodyPara   = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#3a4a6a', lineHeight:1.8, marginBottom:10 };
const legalPara  = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#3a4a6a', lineHeight:1.8, marginBottom:10, fontWeight:600 };
const intro      = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#3a4a6a', lineHeight:1.8, marginBottom:12 };
const bulletList = { margin:'8px 0', paddingLeft:20 };
const bulletItem = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#3a4a6a', lineHeight:1.8, marginBottom:8 };
const scrollHint = { textAlign:'center', padding:'8px', fontFamily:"'Roboto Mono',monospace", fontSize:11, color:AMBER, background:'#fff8e6', flexShrink:0 };
const footer     = { padding:'20px 36px 28px', borderTop:`1.5px solid ${BORDER}`, flexShrink:0 };
const footerNote = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', marginBottom:14, lineHeight:1.6 };
const acceptBtn  = { width:'100%', padding:'14px', background:NAVY, color:AMBER, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, transition:'opacity 0.2s' };
